import { $window } from '../common/globals';
import {
  objectExtend,
  isString,
  isObject,
  isFunction,
  joinUrl,
  decodeBase64,
  getObjectProperty,
  parseQueryString,
} from '../common/utils';
import defaultOptions from './options';
import StorageFactory from '../common/storage';
import CookieStorage from '../common/storage/cookie-storage';
import OAuth1 from './oauth/oauth1';
import OAuth2 from './oauth/oauth2';
import ApiService from '../api/api.service';

// https://github.com/dgrubelic/vue-authenticate 참고.
class Authenticate {
  constructor($http, overrideOptions) {
    let options = objectExtend({}, defaultOptions);
    options = objectExtend(options, overrideOptions);
    let storage = StorageFactory(options);
    let cookieStorage = new CookieStorage(options);

    Object.defineProperties(this, {
      $http: {
        get() {
          return $http;
        },
      },
      options: {
        get() {
          return options;
        },
      },
      storage: {
        get() {
          return storage;
        },
      },
      cookieStorage: {
        get() {
          return cookieStorage;
        },
      },
      tokenName: {
        get() {
          if (this.options.tokenPrefix) {
            return [this.options.tokenPrefix, this.options.tokenName].join('_');
          } else {
            return this.options.tokenName;
          }
        },
      },
      refreshTokenName: {
        get() {
          if (this.options.refreshTokenPrefix) {
            return [
              this.options.refreshTokenPrefix,
              this.options.refreshTokenName,
            ].join('_');
          } else {
            return this.options.refreshTokenName;
          }
        },
      },
    });

    // Setup request interceptors
    if (
      this.options.bindRequestInterceptor &&
      isFunction(this.options.bindRequestInterceptor)
    ) {
      this.options.bindRequestInterceptor.call(this, this);
    } else {
      throw new Error('Request interceptor must be functions');
    }

    // Setup response interceptors
    if (
      this.options.bindResponseInterceptor &&
      isFunction(this.options.bindResponseInterceptor)
    ) {
      this.options.bindResponseInterceptor.call(this, this);
    } else {
      throw new Error('Response interceptor must be functions');
    }
  }

  getRefreshUrl() {
    return this.options.refreshUrl;
  }

  isIntroCheck() {
    return this.options.introViewCheck.call();
  }

  async isAuthenticated(isSkipRetry) {
    let token = this.storage.getItem(this.tokenName);
    //console.log('isAuthenticated', token);
    //console.log('isAuthenticate ', token);
    return new Promise(async (resolve, reject) => {
      // Token is present
      if (token) {
        const c = true;
        if (c) return resolve(true);
        // Token with a valid JWT format XXX.YYY.ZZZ
        if (token.split('.').length === 3) {
          // Could be a valid JWT or an access token with the same format
          try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace('-', '+').replace('_', '/');
            const exp = JSON.parse($window.atob(base64)).exp;
            // JWT with an optonal expiration claims
            if (typeof exp === 'number') {
              const isNonExpired =
                Math.round(new Date().getTime() / 1000) < exp;

              if (!isNonExpired && !isSkipRetry) {
                await this.refresh()
                  .then(() => resolve(true))
                  .catch(() => resolve(false));
              }

              return resolve(isNonExpired);
            }
          } catch (e) {
            // Pass: Non-JWT token that looks like JWT
            return resolve(true);
          }
        }
        // Pass: All other tokens
        return resolve(true);
      }
      return resolve(false);
    });
  }

  getTokenHeader(isRefresh) {
    return isRefresh
      ? this.options.refreshTokenHeader
      : this.options.tokenHeader;
  }
  getTokenValue(isRefresh) {
    const tokenType = isRefresh
      ? this.options.refreshTokenType
      : this.options.tokenType;
    return [tokenType, this.getToken(isRefresh)].join(' ').trim();
  }

  getToken(isRefresh) {
    return this.storage.getItem(
      isRefresh ? this.refreshTokenName : this.tokenName,
    );
  }

  setToken(response, tokenPath, refreshTokenPath) {
    if (response[this.options.responseDataKey]) {
      response = response[this.options.responseDataKey];
    }
    //console.log(response);

    //accessToken
    const responseTokenPath = tokenPath || this.options.tokenPath;
    const token = getObjectProperty(response, responseTokenPath);

    if (token) {
      this.storage.setItem(this.tokenName, token);
    }

    //refreshToken
    const responseRefreshTokenPath =
      refreshTokenPath || this.options.refreshTokenPath;
    const refreshToken = getObjectProperty(response, responseRefreshTokenPath);

    if (refreshToken) {
      this.storage.setItem(this.refreshTokenName, refreshToken);
    }
  }

  getPayload(isRefresh) {
    const tokenName = isRefresh ? this.refreshTokenName : this.tokenName;
    const token = this.storage.getItem(tokenName);

    if (token && token.split('.').length === 3) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(decodeBase64(base64));
      } catch (e) {
        /* empty */
      }
    }
  }

  login(user, requestOptions) {
    requestOptions = requestOptions || {};
    requestOptions.url = requestOptions.url
      ? requestOptions.url
      : joinUrl(this.options.baseUrl, this.options.loginUrl);
    requestOptions[this.options.requestDataKey] =
      user || requestOptions[this.options.requestDataKey];
    requestOptions.method = requestOptions.method || 'POST';
    requestOptions.withCredentials =
      requestOptions.withCredentials || this.options.withCredentials;

    return ApiService.request(requestOptions)
      .then(response => {
        //console.log('lib/authenticate/login', response);
        this.setToken(response);
        return response;
      })
      .catch(function(error) {
        if (error.response) {
          // 요청이 이루어졌으며 서버가 2xx의 범위를 벗어나는 상태 코드로 응답했습니다.
          console.log(error.response.data);
          console.log(error.response.status);
          console.log(error.response.headers);
        } else if (error.request) {
          // 요청이 이루어 졌으나 응답을 받지 못했습니다.
          // `error.request`는 브라우저의 XMLHttpRequest 인스턴스 또는
          // Node.js의 http.ClientRequest 인스턴스입니다.
          console.log(error.request);
        } else {
          // 오류를 발생시킨 요청을 설정하는 중에 문제가 발생했습니다.
          console.log('Error', error.message);
        }
        console.log(error.config);
        return Promise.reject(error);
      });
  }

  logout(requestOptions) {
    if (!this.isAuthenticated()) {
      return Promise.reject(
        new Error('There is no currently authenticated user'),
      );
    }

    requestOptions = requestOptions || {};

    if (
      (requestOptions.url || this.options.logoutUrl) &&
      !requestOptions.purge
    ) {
      requestOptions.url = requestOptions.url
        ? requestOptions.url
        : joinUrl(this.options.baseUrl, this.options.logoutUrl);
      requestOptions.method = requestOptions.method || 'POST';
      requestOptions[this.options.requestDataKey] =
        requestOptions[this.options.requestDataKey] || undefined;
      requestOptions.withCredentials =
        requestOptions.withCredentials || this.options.withCredentials;

      return ApiService.request(requestOptions).then(response => {
        this.storage.removeItem(this.tokenName);
        this.storage.removeItem(this.refreshTokenName);
        return Promise.resolve();
      });
    } else {
      this.storage.removeItem(this.tokenName);
      this.storage.removeItem(this.refreshTokenName);
      return Promise.resolve();
    }
  }

  refresh(requestOptions) {
    requestOptions = requestOptions || {};

    requestOptions.url = requestOptions.url
      ? requestOptions.url
      : joinUrl(this.options.baseUrl, this.options.refreshUrl);
    requestOptions.headers = {};
    requestOptions.headers[this.getTokenHeader(true)] = this.getTokenValue(
      true,
    );
    requestOptions.method = requestOptions.method || 'GET';
    requestOptions.withCredentials =
      requestOptions.withCredentials || this.options.withCredentials;
    //console.log('refresh try ', requestOptions);
    return ApiService.request(requestOptions).then(response => {
      this.setToken(response);
    });
  }

  register(user, requestOptions) {
    requestOptions = requestOptions || {};
    requestOptions.url = requestOptions.url
      ? requestOptions.url
      : joinUrl(this.options.baseUrl, this.options.registerUrl);
    requestOptions[this.options.requestDataKey] =
      user || requestOptions[this.options.requestDataKey];
    requestOptions.method = requestOptions.method || 'POST';
    requestOptions.withCredentials =
      requestOptions.withCredentials || this.options.withCredentials;

    return ApiService.request(requestOptions)
      .then(response => {
        this.setToken(response);
        return response;
      })
      .catch(error => {
        console.log(error);
      });
  }

  getUser(user, requestOptions) {
    requestOptions = requestOptions || {};
    requestOptions.url = requestOptions.url
      ? requestOptions.url
      : joinUrl(this.options.baseUrl, this.options.userInfoUrl);
    requestOptions[this.options.requestDataKey] =
      user || requestOptions[this.options.requestDataKey];
    requestOptions.method = requestOptions.method || 'GET';
    requestOptions.withCredentials =
      requestOptions.withCredentials || this.options.withCredentials;

    return ApiService.request(requestOptions).then(response => {
      this.setToken(response);
      return response;
    });
  }

  loginProvider(provider, userData) {
    return new Promise((resolve, reject) => {
      var providerConfig = this.options.providers[provider];
      if (!providerConfig) {
        return reject(new Error('Unknown provider'));
      }

      let providerInstance;
      switch (providerConfig.oauthType) {
        case '1.0':
          providerInstance = new OAuth1(
            this.$http,
            this.storage,
            providerConfig,
            this.options,
          );
          break;
        case '2.0':
          providerInstance = new OAuth2(
            this.$http,
            this.storage,
            providerConfig,
            this.options,
          );
          break;
        default:
          return reject(new Error('Invalid OAuth type'));
      }

      return providerInstance
        .init(userData)
        .then(response => {
          this.setToken(response, providerConfig.tokenPath);

          if (this.isAuthenticated()) {
            return resolve(response);
          } else {
            return reject(new Error('Authentication failed'));
          }
        })
        .catch(err => reject(err));
    });
  }

  //모바일 로그인
  loginMobile(user, requestOptions) {
    requestOptions = requestOptions || {};
    requestOptions.url = requestOptions.url
      ? requestOptions.url
      : joinUrl(this.options.baseUrl, this.options.mobileAuthUrl);
    requestOptions[this.options.requestDataKey] =
      user || requestOptions[this.options.requestDataKey];
    requestOptions.method = requestOptions.method || 'POST';
    requestOptions.withCredentials =
      requestOptions.withCredentials || this.options.withCredentials;

    return ApiService.request(requestOptions).then(response => {
      this.setToken(response);
      return response;
    });
  }

  link(provider, userData) {
    return new Promise((resolve, reject) => {
      var providerConfig = this.options.providers[provider];
      if (!providerConfig) {
        return reject(new Error('Unknown provider'));
      }

      let providerInstance;
      switch (providerConfig.oauthType) {
        case '1.0':
          providerInstance = new OAuth1(
            this.$http,
            this.storage,
            providerConfig,
            this.options,
          );
          break;
        case '2.0':
          providerInstance = new OAuth2(
            this.$http,
            this.storage,
            providerConfig,
            this.options,
          );
          break;
        default:
          return reject(new Error('Invalid OAuth type'));
      }

      return providerInstance
        .init(userData)
        .then(response => {
          if (response[this.options.responseDataKey]) {
            response = response[this.options.responseDataKey];
          }

          resolve(response);
        })
        .catch(reject);
    });
  }

  withdraw() {
    if (!this.isAuthenticated()) {
      return Promise.reject(
        new Error('There is no currently authenticated user'),
      );
    }

    return ApiService.delete(`user`).then(response => {
      this.storage.removeItem(this.tokenName);
      this.storage.removeItem(this.refreshTokenName);
      return Promise.resolve(response);
    });
  }
}

export default Authenticate;
